/* ═══════════════════════════════════════════════════════════════
   VerifyEmailBanner — shown at the top of the app when the
   signed-in user hasn't verified their email yet.

   Renders nothing when emailVerified === true. Otherwise shows a
   dismissable banner with a "Enter code" CTA that expands an
   inline 6-digit input, plus a "Resend" link.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import { AuthService, type AuthUser } from '../services/authService';

interface Props {
  authUser: AuthUser;
  onVerified?: () => void;
}

export function VerifyEmailBanner({ authUser, onVerified }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<
    'idle' | 'verifying' | 'resending' | 'resent' | 'verified' | 'error'
  >('idle');
  const [error, setError] = useState('');

  if (authUser.emailVerified) return null;
  if (status === 'verified') return null;

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Enter the 6-digit code');
      setStatus('error');
      return;
    }
    setStatus('verifying');
    setError('');
    const r = await AuthService.verifyEmail(authUser.email, code);
    if (r.error) {
      setError(r.error);
      setStatus('error');
      return;
    }
    setStatus('verified');
    onVerified?.();
  };

  const handleResend = async () => {
    setStatus('resending');
    setError('');
    const r = await AuthService.requestVerification(authUser.email);
    if (r.error) {
      setError(r.error);
      setStatus('error');
      return;
    }
    setStatus('resent');
    setTimeout(() => setStatus('idle'), 4000);
  };

  return (
    <div
      style={{
        padding: '10px 20px',
        background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
        borderBottom: '1px solid color-mix(in srgb, var(--color-warning) 45%, transparent)',
        color: 'var(--color-warning)',
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 14 }}>✉</span>
      <span style={{ flex: 1, minWidth: 200 }}>
        <strong>Verify your email.</strong> We sent a 6-digit code to <b>{authUser.email}</b>.
      </span>

      {!expanded ? (
        <>
          <button onClick={() => setExpanded(true)} style={btnPrimary}>
            Enter code
          </button>
          <button onClick={handleResend} disabled={status === 'resending'} style={btnGhost}>
            {status === 'resending' ? 'Sending…' : status === 'resent' ? 'Code sent ✓' : 'Resend'}
          </button>
        </>
      ) : (
        <>
          <input
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleVerify();
            }}
            placeholder="000000"
            autoFocus
            style={{
              width: 110,
              padding: '4px 10px',
              border: '1px solid var(--color-warning)',
              borderRadius: 4,
              fontSize: 15,
              fontWeight: 700,
              textAlign: 'center',
              letterSpacing: '0.3em',
              fontFamily: "'JetBrains Mono', monospace",
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              outline: 'none',
            }}
          />
          <button
            onClick={handleVerify}
            disabled={status === 'verifying' || code.length !== 6}
            style={{
              ...btnPrimary,
              opacity: status === 'verifying' || code.length !== 6 ? 0.6 : 1,
            }}
          >
            {status === 'verifying' ? 'Verifying…' : 'Verify'}
          </button>
          <button
            onClick={() => {
              setExpanded(false);
              setCode('');
              setError('');
              setStatus('idle');
            }}
            style={btnGhost}
          >
            Cancel
          </button>
          <button onClick={handleResend} disabled={status === 'resending'} style={btnGhost}>
            {status === 'resending' ? 'Sending…' : status === 'resent' ? 'Sent ✓' : 'Resend'}
          </button>
        </>
      )}

      {error && (
        <span style={{ color: 'var(--color-danger)', fontSize: 12, width: '100%' }}>{error}</span>
      )}
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '5px 14px',
  borderRadius: 4,
  border: 'none',
  background: 'var(--color-warning)',
  color: 'var(--color-text-inverse)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: '5px 10px',
  borderRadius: 4,
  border: '1px solid transparent',
  background: 'transparent',
  color: 'var(--color-warning)',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
};
